'use client';
import { isArray } from '../../array/array.js';
import { isFunction } from '../isFunction/isFunction.js';

/**
 * Проверяет, является ли `subject` объектом.
 *
 * @remarks
 * Type-safe обёртка над `typeof`.
 *
 * @note
 * Также исключает случай, когда `null` интерпретируется как объект.
 *
 * @typeParam R - Тип объекта произвольной структуры / массива значений произвольного типа.
 * @param subject - Субъект проверки произвольного типа.
 *
 * @primaryExample
 * isObject({}) // true
 * isObject([]) // true
 * isObject(null) // false
 *
 * if (isObject<MyType>(myObject)) {
 *     // Здесь для myObject будет выведен тип MyType.
 * }
 *
 * @stable
 * */ const isObject = (subject)=>typeof subject === 'object' && subject !== null;
/**
 * Выполняет проверку того, что `subject` существует, является объектом и не является массивом.
 *
 * @remarks
 * Type-safe обёртка над `typeof` и `isArray`.
 *
 * @note
 * Также исключает случай, когда значение может быть приведено к false с помощью `!!`.
 *
 * @typeParam R - Тип объекта произвольной структуры.
 * @param subject - Субъект проверки произвольного типа.
 *
 * @primaryExample
 * isNonArrayObject({}) // true
 * isNonArrayObject([]) // false
 *
 * @stable
 * */ const isNonArrayObject = (subject)=>!!subject && typeof subject === 'object' && !isArray(subject);
/**
 * Проверяет, является ли `subject` итератором.
 *
 * @remarks
 * Использует функцию `isObject` и проверяет, что `Symbol.iterator` на `subject` является функцией.
 *
 * @typeParam T - Параметр `IteratorYieldResult<T>`, .
 * @param subject - Субъект проверки произвольного типа.
 *
 * @primaryExample
 * isIterable({}) // true
 * isIterable([]) // false
 *
 * @stable
 * */ const isIterable = (subject)=>{
    // @ts-expect-error - subject может иметь итератор.
    return isObject(subject) && isFunction(subject[Symbol.iterator]);
};
/**
 * Проверяет, что `subject` является объектом, который либо не имеет прототипа, либо его прототип - Object.prototype.
 *
 * @param subject - Субъект проверки произвольного типа.
 *
 * @stable
 */ const isPlainObject = (subject)=>{
    if (Object.prototype.toString.call(subject) !== '[object Object]') {
        return false;
    }
    const prototype = Object.getPrototypeOf(subject);
    return prototype === null || prototype === Object.prototype;
};

export { isIterable, isNonArrayObject, isObject, isPlainObject };
//# sourceMappingURL=isObject.js.map
